import React, {ReactNode, Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import {
  ApiResponseGetMaintenanceRecordTimelineResponse,
  CreatedMaintenanceRecordRequest,
  GetMaintenanceRecordResponse,
  UpdatedMaintenanceRecordRequest,
} from '@di-shared/api/di/__generated';
import Popper from '@di-shared/features/common/Popper';
import Tab from '@di-shared/features/common/Tab';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ERR_4090_CONFLICT} from '@di-shared/features/diag/exchange/history/DiagExchangeHistoryDetailEdit';
import useSestName from '@di-shared/features/diag/sest/useSestName';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useQueryClient, useSuspenseQuery} from '@tanstack/react-query';

interface DiagExchangeHistoryProps {
  vin: string;
  selectedTab: ConsumableTabServerType;

  onUpdateTabFilter(tab: ConsumableTabServerType): void;
}

// 탭 배열 (필요시 순서대로 배열)
export const CONSUMABLE_TAB_ALL = 'ALL' as const;

export type ConsumableTabServerType = CreatedMaintenanceRecordRequest.sestCode | 'ALL';

const CONSUMABLE_TABS: ConsumableTabServerType[] = [
  CONSUMABLE_TAB_ALL,
  CreatedMaintenanceRecordRequest.sestCode._01,
  CreatedMaintenanceRecordRequest.sestCode._02,
  CreatedMaintenanceRecordRequest.sestCode._21,
  CreatedMaintenanceRecordRequest.sestCode._09,
  CreatedMaintenanceRecordRequest.sestCode._03,
];

const ConsumableIconMap: Record<CreatedMaintenanceRecordRequest.sestCode, React.ReactNode> = {
  [CreatedMaintenanceRecordRequest.sestCode._01]: <span className="ic ic_engine_oil_24px" />,
  [CreatedMaintenanceRecordRequest.sestCode._02]: <span className="ic ic_air_cleaner_filter_24px" />,
  [CreatedMaintenanceRecordRequest.sestCode._03]: <span className="ic ic_air_conditioner_24px" />,
  [CreatedMaintenanceRecordRequest.sestCode._04]: <span className="ic ic_spark_plug_24px" />,
  [CreatedMaintenanceRecordRequest.sestCode._09]: <span className="ic ic_brake_oil_24px" />,
  [CreatedMaintenanceRecordRequest.sestCode._18]: <span className="ic ic_battery_24px" />,
  [CreatedMaintenanceRecordRequest.sestCode._21]: <span className="ic ic_coolant_24px" />,
};

function DiagExchangeHistory(props: DiagExchangeHistoryProps) {
  const {t} = useInferableTranslation();
  return (
    <ErrorBoundary
      fallback={
        <section
          className="section empty_section"
          style={{display: 'flex'}}
        >
          <span className="ic ic_caution_24px"></span>
          <strong className="empty_section_title">{t('T30575')}</strong>
          <p className="empty_section_sub_text type_xsmall">{t('T30205')}</p>
        </section>
      }
    >
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

const INITIAL_VISIBLE_ITEM_COUNT = 5;

function filterBySestCode(
  recordsByDate: Array<[string, Array<GetMaintenanceRecordResponse>]>,
  targetSestCode: CreatedMaintenanceRecordRequest.sestCode
): Array<[string, Array<GetMaintenanceRecordResponse>]> {
  return recordsByDate
    .map(
      ([dateKey, records]) =>
        [dateKey, records.filter((record) => record.sestCode === targetSestCode)] as [string, Array<GetMaintenanceRecordResponse>]
    )
    .filter(([, filteredRecords]) => filteredRecords.length > 0);
}

function Inner({vin, selectedTab, onUpdateTabFilter}: DiagExchangeHistoryProps) {
  const {t} = useInferableTranslation();
  const {sestNameLabels} = useSestName();

  const {data: timelineData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.maintenanceRecordController.getMaintenanceRecordTimeline), vin],
    queryFn: () => diApi.maintenanceRecordController.getMaintenanceRecordTimeline({vin}),
    select: (data: ApiResponseGetMaintenanceRecordTimelineResponse) => {
      return {
        ...data,
        data: {
          ...data.data,
          // object key가 20251203, 20251101와 같이 내려올 때, js가 자동으로 sorting 해버리는 이슈로 인해 FE에서 한번 더 정렬하는 작업
          sortedRecordsByDate: Object.entries(data.data.recordsByDate).sort(([a], [b]) => b.localeCompare(a)),
        },
      };
    },
  });

  const canExpand = timelineData.data.totalRecords > INITIAL_VISIBLE_ITEM_COUNT;

  return (
    <>
      <section className="section section_change_history">
        <div className="tab_box">
          <Tab
            element="ul"
            className="tab_list"
            selectedValue={selectedTab}
          >
            {CONSUMABLE_TABS.map((tab) => (
              <Tab.Item
                key={tab}
                value={tab}
                element="li"
                activeClassName="is_active"
                className="tab_item"
              >
                <button
                  type="button"
                  className="btn btn_tab"
                  onClick={() => onUpdateTabFilter(tab)}
                >
                  {tab === CONSUMABLE_TAB_ALL ? t('T30587') : sestNameLabels[tab]}
                </button>
              </Tab.Item>
            ))}
          </Tab>
        </div>
      </section>
      {selectedTab === CONSUMABLE_TAB_ALL ? (
        <HistoryList
          vin={vin}
          items={timelineData.data.sortedRecordsByDate}
          canExpand={canExpand}
        />
      ) : (
        <HistoryList
          vin={vin}
          items={filterBySestCode(timelineData.data.sortedRecordsByDate, selectedTab)}
          canExpand={false}
        />
      )}
    </>
  );
}

function Loading() {
  const {t} = useInferableTranslation();
  const {sestNameLabels} = useSestName();
  return (
    <>
      <section className="section section_change_history">
        <div className="tab_box">
          <ul className="tab_list ">
            {CONSUMABLE_TABS.map((tab) => (
              <li
                className={clsx('tab_item', {is_active: tab === CONSUMABLE_TABS[0]})}
                key={tab}
              >
                <button
                  type="button"
                  className="btn btn_tab"
                  onClick={() => {}}
                >
                  {tab === CONSUMABLE_TAB_ALL ? t('T30587') : sestNameLabels[tab]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="section section_change_history ">
        <div className="section_container_header">&nbsp;</div>
        {Array.from({length: 3}).map((_, index) => (
          <div
            key={index}
            className="section_container skeleton"
            style={{height: 132}}
          />
        ))}
      </section>
    </>
  );
}

export default DiagExchangeHistory;

function getVisibleItems(
  recordsByDate: Array<[string, Array<GetMaintenanceRecordResponse>]>,
  maxVisibleCount: number
): Array<[string, Array<GetMaintenanceRecordResponse>]> {
  let accumulatedCount = 0;
  const visibleItemsByDate: Array<[string, Array<GetMaintenanceRecordResponse>]> = [];

  for (const [dateKey, records] of recordsByDate) {
    if (accumulatedCount >= maxVisibleCount) {
      break;
    }
    const remainingCount = maxVisibleCount - accumulatedCount;
    const visibleRecords = records.slice(0, remainingCount);

    if (visibleRecords.length > 0) {
      visibleItemsByDate.push([dateKey, visibleRecords]);
      accumulatedCount += visibleRecords.length;
    }
  }

  return visibleItemsByDate;
}

interface HistoryListProps {
  vin: string;
  items: Array<[string, Array<GetMaintenanceRecordResponse>]>;
  canExpand: boolean;
}

function HistoryList({items, vin, canExpand}: HistoryListProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const navigate = useNavigate();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const queryClient = useQueryClient();
  const {resolveError} = useApiError();
  const {sestNameLabels} = useSestName();
  const {getErrorCode} = useApiError();
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  /**
   * 펼칠 수 있는 갯수가 있고 (canExpand)
   * 펼쳐지지 않았다면 (!isExpanded)
   * INITIAL_VISIBLE_ITEM_COUNT 개 만큼 필터링 함
   */
  const visibleItems = canExpand && !isExpanded ? getVisibleItems(items, INITIAL_VISIBLE_ITEM_COUNT) : items;

  const [updateCurrentHistory] = usePendingHandler(
    async (params: {vin: string; recordId: number; requestBody: UpdatedMaintenanceRecordRequest}) => {
      await diApi.maintenanceRecordController.updateMaintenanceRecord(params);
      await queryClient.invalidateQueries({queryKey: [getQueryKey(diApi.maintenanceRecordController.getMaintenanceRecordTimeline)]});
    },
    async (error) => {
      if (getErrorCode(error) === ERR_4090_CONFLICT) {
        const isConfirm = await openConfirm({
          title: t('T30590'),
          message: t('T30591'),
          okText: t('T30592'),
          cancelText: t('T30593'),
        });
        if (isConfirm) {
          backOrClose();
        }
        return;
      }
      const [execute] = await resolveError(error);
      execute();
    }
  );

  if (visibleItems.length === 0) {
    return (
      <>
        <section className="section empty_section">
          <span className="ic ic_caution_24px"></span>
          <strong className="empty_section_title">{t('T30576')}</strong>
        </section>
        <section className="section section_change_history">
          <p className="desc_more">{t('T30577_$year', {year: 10})}</p>
        </section>
      </>
    );
  }
  return (
    <>
      {visibleItems.map(([key, records]) => {
        const parsedDate = parseDateWithFormat(key);
        return (
          <section
            className="section section_change_history"
            key={key}
          >
            <div className="section_container_header">
              {parsedDate && <h3 className="title_date">{CldrDatetimeFormatter.format(parsedDate, 'yMMMd')}</h3>}
            </div>
            {records.map(({id, source, sestCode, replaceOdometer, isActive, replaceDate}) =>
              source === GetMaintenanceRecordResponse.source.SERVICE_CENTER ? (
                <ServiceCenterHistoryItem
                  key={id}
                  icon={ConsumableIconMap[sestCode as CreatedMaintenanceRecordRequest.sestCode]}
                  sestName={sestNameLabels[sestCode as CreatedMaintenanceRecordRequest.sestCode]}
                  odometer={convertDistanceValue(replaceOdometer)}
                  distanceUnit={convertDistanceSymbol(replaceOdometer)}
                  isWarning={false}
                  isCurrentHistory={isActive}
                  onSelectCurrentHistory={() =>
                    updateCurrentHistory({
                      vin,
                      recordId: id,
                      requestBody: {
                        vin,
                        sestCode: sestCode as UpdatedMaintenanceRecordRequest.sestCode,
                        replaceOdometer,
                        replaceDate,
                        source,
                        isActive: true,
                      },
                    })
                  }
                />
              ) : (
                <ManualInputHistoryItem
                  key={id}
                  icon={ConsumableIconMap[sestCode as CreatedMaintenanceRecordRequest.sestCode]}
                  sestName={sestNameLabels[sestCode as CreatedMaintenanceRecordRequest.sestCode]}
                  odometer={convertDistanceValue(replaceOdometer)}
                  distanceUnit={convertDistanceSymbol(replaceOdometer)}
                  isWarning={false}
                  isCurrentHistory={isActive}
                  onEdit={() => navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY_DETAIL_EDIT.getFullPath(vin, id.toString()))}
                  onSelectCurrentHistory={() =>
                    updateCurrentHistory({
                      vin,
                      recordId: id,
                      requestBody: {
                        vin,
                        sestCode: sestCode as UpdatedMaintenanceRecordRequest.sestCode,
                        replaceOdometer,
                        replaceDate,
                        source,
                        isActive: true,
                      },
                    })
                  }
                />
              )
            )}
          </section>
        );
      })}

      <section className="section section_change_history">
        {canExpand && !isExpanded && (
          <button
            type="button"
            className="btn btn_round btn_round_outline btn_more"
            onClick={() => setIsExpanded(true)}
          >
            <span className="ic ic_arrow_down_12px"></span>
            {t('T20134')}
          </button>
        )}

        <p className="desc_more">{t('T30577_$year', {year: 10})}</p>
      </section>
    </>
  );
}

interface ServiceCenterHistoryItemProps {
  icon: ReactNode;
  sestName: string;
  odometer: number | string;
  distanceUnit: string;
  isWarning?: boolean;
  isCurrentHistory: boolean;

  onSelectCurrentHistory(): void;
}

function ServiceCenterHistoryItem({
  icon,
  sestName,
  odometer,
  distanceUnit,
  isWarning,
  isCurrentHistory,
  onSelectCurrentHistory,
}: ServiceCenterHistoryItemProps) {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="expendable_detail">
        <ul className="expendable_list">
          <li className={clsx('expendable_item', {type_warning: isWarning})}>
            <div className="expendable_icon_box">{icon}</div>
            <div className="expendable_info">
              <span className="expendable_info_title">{sestName}</span>
              <span className="expendable_info_base">
                {formatNumberWithCommas(odometer)}
                {distanceUnit}&nbsp;{t('T30296')}&nbsp;<span className="expendable_data">{t('T30301')}</span>
              </span>
            </div>

            <Popper
              placement="bottom"
              className="tooltip"
              marginLeft={3}
              tooltip={
                <>
                  <strong className="tooltip_title">{t('T30586')}</strong>
                  {/**
                   * [2025-11-18:Jeyoung Lee] FIXME: 정비소 브랜드 , 고객센터 번호 확인
                   */}
                  <p className="tooltip_text">{t('T30579')}</p>
                  <ul className="tooltip_list">
                    <li className="tooltip_item">{t('T30580')}</li>
                    <li className="tooltip_item">{t('T30581_$tel', {tel: import.meta.env.VITE_EMERGENCY_TEL_BRAND})}</li>
                  </ul>
                </>
              }
            >
              <div className="tooltip_area tooltip_area_single">
                <button
                  type="button"
                  className="btn_tooltip"
                  aria-controls="tooltipStatement01"
                  aria-expanded="false"
                  aria-describedby="tooltipBox01"
                >
                  <span
                    className="ic ic_information_16px"
                    aria-hidden="true"
                  />
                  <span className="blind">{t('T10044')}</span>
                </button>
              </div>
            </Popper>
          </li>
        </ul>
        {isCurrentHistory && (
          <p className="status_desc">
            <span className="ic ic_check_marker" />
            {t('T30582')}
          </p>
        )}
      </div>
      {!isCurrentHistory && (
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => onSelectCurrentHistory()}
        >
          {t('T30583')}
        </button>
      )}
    </div>
  );
}

interface ManualInputHistoryItemProps {
  icon: ReactNode;
  sestName: string;
  odometer: number | string;
  distanceUnit: string;
  isWarning?: boolean;
  isCurrentHistory: boolean;
  onEdit: () => void;

  onSelectCurrentHistory(): void;
}

function ManualInputHistoryItem({
  icon,
  sestName,
  odometer,
  distanceUnit,
  isWarning,
  isCurrentHistory,
  onEdit,
  onSelectCurrentHistory,
}: ManualInputHistoryItemProps) {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="expendable_detail">
        <ul className="expendable_list">
          <li className={clsx('expendable_item', {type_warning: isWarning})}>
            <div className="expendable_icon_box">{icon}</div>
            <div className="expendable_info">
              <span className="expendable_info_title">{sestName}</span>
              <span className="expendable_info_base">
                {formatNumberWithCommas(odometer)}
                {distanceUnit}&nbsp;{t('T30296')}&nbsp;<span className="expendable_data">{t('T30584')}</span>
              </span>
            </div>
            <button
              type="button"
              className="btn_edit"
              onClick={() => onEdit()}
            >
              {t('T30585')}
            </button>
          </li>
        </ul>
        {isCurrentHistory && (
          <p className="status_desc">
            <span className="ic ic_check_marker" />
            {t('T30582')}
          </p>
        )}
      </div>
      {!isCurrentHistory && (
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={() => onSelectCurrentHistory()}
        >
          {t('T30583')}
        </button>
      )}
    </div>
  );
}
